import { Button } from '@casumo/dojo-react';
import { memo } from 'react';
import AddressButton from '../components/buttons/AddressButton.tsx';
import CountrySelectorButton from '../components/buttons/countrySelectorButton.tsx';
import DepositButton from '../components/buttons/depositButton.tsx';
import DepositLimitsButton from '../components/buttons/depositLimitsButton';
import LoginButton from '../components/buttons/loginButton';
import MultiMarketingConsentButton from '../components/buttons/multiMarketingConsentButton.tsx';
import NoBonusButton from '../components/buttons/noBonusButton';
import RegisterPrimaryButton from '../components/buttons/registerPrimaryButton.tsx';
import RegisterSecondaryButton from '../components/buttons/registerSecondaryButton.tsx';
import ReviewDetailsButton from '../components/buttons/reviewDetailsButton.tsx';
import SecondaryDepositLimitsButton from '../components/buttons/secondaryDepositLimitsButton.tsx';
import { ButtonType, TStep } from '../types/types';
import { TEST_ID } from '../utils/constants.ts';

type TButtonRendererProps = {
  currentStepConfig: TStep;
  handleNext: (args: { gtmPayload?: Record<string, string>; redirectTo?: string }) => void;
}

type TButtonMap = {
  label?: string;
  gtmPayload?: Record<string, string>;
  redirectTo?: string;
  name?: string;
}

const ButtonRenderer = ({ currentStepConfig, handleNext }: TButtonRendererProps) => {
  const buttonMap = (
    props: TButtonMap) => {
    const { label, gtmPayload, redirectTo } = props;

    return {
      [ButtonType.Redirect]: (
        <Button
          className="focus:outline-none focus:ring-purpleblue focus:ring-offset-0 focus:ring-0.5"
          width="full"
          onClick={() => handleNext({ gtmPayload, redirectTo })}
          data-testid={TEST_ID.BUTTON_PRIMARY}
        >
          {label}
        </Button>
      ),
      [ButtonType.SecondaryRedirect]: (
        <Button
          className="focus:outline-none focus:ring-purpleblue focus:ring-offset-0 focus:ring-0.5"
          variant="tertiary"
          width="full"
          onClick={() => handleNext({ gtmPayload, redirectTo })}
          data-testid={TEST_ID.BUTTON_SECONDARY}
        >
          {label}
        </Button>
      ),
      [ButtonType.Login]:         <LoginButton {...props} />,
      [ButtonType.DepositLimits]: (
        <DepositLimitsButton
          {...props}
          handleNext={handleNext}
        />
      ),
      [ButtonType.NoBonus]: (
        <NoBonusButton {...props}
          handleNext={handleNext} />
      ),
      [ButtonType.CountrySelector]: (
        <CountrySelectorButton {...props} handleNext={handleNext} />
      ),
      [ButtonType.SecondaryDepositLimits]: (
        <SecondaryDepositLimitsButton {...props} handleNext={handleNext} />
      ),
      [ButtonType.RegisterPrimary]: (
        <RegisterPrimaryButton {...props} handleNext={handleNext} />
      ),
      [ButtonType.RegisterSecondary]: (
        <RegisterSecondaryButton {...props} handleNext={handleNext} />
      ),
      [ButtonType.ReviewDetails]: (
        <ReviewDetailsButton handleNext={handleNext} {...props} />
      ),
      [ButtonType.AddressButton]: (
        <AddressButton handleNext={handleNext} {...props} />
      ),
      [ButtonType.MultiMarketingConsent]: (
        <MultiMarketingConsentButton handleNext={handleNext} {...props} />
      ),
      [ButtonType.Deposit]: (
        <DepositButton handleNext={handleNext} {...props} />
      ),
    };
  };

  return (
    <div className="flex flex-col justify-center items-center gap-3xs animate-fadeIn pointer-events-auto"
      key={currentStepConfig.name}>
      {
        currentStepConfig?.secondaryButton?.fieldType
        && buttonMap(
          {
            label:      currentStepConfig.secondaryButton.label,
            gtmPayload: currentStepConfig.secondaryButton.gtmPayload,
            redirectTo: currentStepConfig.secondaryButton.redirectTo,
          },
        )[currentStepConfig.secondaryButton.fieldType]
      }
      {
        currentStepConfig?.primaryButton?.fieldType
        && buttonMap(
          {
            label:      currentStepConfig.primaryButton.label,
            gtmPayload: currentStepConfig.primaryButton.gtmPayload,
            redirectTo: currentStepConfig.primaryButton.redirectTo,
            name:       currentStepConfig.primaryButton.name,
          },
        )[currentStepConfig.primaryButton.fieldType]
      }
    </div>
  );
};

const Memoized = memo(ButtonRenderer);
Memoized.displayName = 'ButtonRenderer';

export default Memoized;
